import React, { useEffect, useState } from 'react';
import {
    applyAiPrediction,
    deleteAiPrediction,
    deletePipelineJob,
    generateOneShotPrompt,
    getAgents,
    getAiPredictions,
    getAiRuntimeConfig,
    getAiServerStatus,
    getPipelineJob,
    getPipelineJobs,
    runPipelineAsync,
    updateAiRuntimeConfigHost,
    warmupAiModel,
} from '../../api';
import RaceSelector from '../../components/RaceSelector';
import { useAppAlert } from '../../components/AppAlerts';
import AiPredictionPerformanceChart from './AiPredictionPerformanceChart';

const ACTIVE_PIPELINE_JOB_STORAGE_KEY = 'superadmin-ai-active-pipeline-job';

function safeJsonString(input) {
    if (!input) return null;
    if (typeof input === 'object') return JSON.stringify(input, null, 2);
    try {
        return JSON.stringify(JSON.parse(input), null, 2);
    } catch (_err) {
        return String(input);
    }
}

function formatPredictionContent(row) {
    if (row?.responseJson) {
        return JSON.stringify(row.responseJson, null, 2);
    }
    return row?.finalResponse || '';
}

function getPromptText(row) {
    if (Array.isArray(row?.stepResults) && row.stepResults.length > 0) {
        return row.stepResults[0]?.prompt || row.stepResults[row.stepResults.length - 1]?.prompt || '';
    }
    return row?.stepResults ? JSON.stringify(row.stepResults, null, 2) : '';
}

function getResultText(row) {
    if (Array.isArray(row?.stepResults) && row.stepResults.length > 0) {
        return JSON.stringify(row.stepResults, null, 2);
    }
    return formatPredictionContent(row);
}

export default function SuperAdminAI() {
    const [aiRace, setAiRace] = useState('');
    const [pipelines, setPipelines] = useState([]);
    const [selectedKey, setSelectedKey] = useState('');
    const [running, setRunning] = useState(false);
    const [checkingServer, setCheckingServer] = useState(false);
    const [warmingModel, setWarmingModel] = useState(false);
    const [warmupMessage, setWarmupMessage] = useState('');
    const [aiHostInput, setAiHostInput] = useState('');
    const [loadingPredictions, setLoadingPredictions] = useState(false);
    const [predictions, setPredictions] = useState([]);
    const [loadingJobs, setLoadingJobs] = useState(false);
    const [jobs, setJobs] = useState([]);
    const [generatingPrompt, setGeneratingPrompt] = useState(false);
    const [generatedPrompt, setGeneratedPrompt] = useState('');
    const [generatedPromptMeta, setGeneratedPromptMeta] = useState(null);
    const [serverStatus, setServerStatus] = useState({ online: false, message: 'Not checked yet' });
    const [error, setError] = useState('');
    const [extraNotes, setExtraNotes] = useState('');
    const [applyingPredictionId, setApplyingPredictionId] = useState(null);
    const [deletingPredictionId, setDeletingPredictionId] = useState(null);
    const [applyMessage, setApplyMessage] = useState('');
    const [activeJob, setActiveJob] = useState(null);
    const [jobWarning, setJobWarning] = useState('');
    const [jobPollingTick, setJobPollingTick] = useState(0);
    const [deletingJobId, setDeletingJobId] = useState(null);
    const notify = useAppAlert();

    const pushError = (message) => {
        const text = message || 'Something went wrong';
        setError(text);
        notify(text, 'error');
    };

    const pushSuccess = (message) => {
        if (!message) return;
        notify(message, 'success');
    };

    useEffect(() => {
        const raw = window.localStorage.getItem(ACTIVE_PIPELINE_JOB_STORAGE_KEY);
        if (!raw) return;

        try {
            const persisted = JSON.parse(raw);
            if (persisted?.id) {
                setActiveJob({
                    id: persisted.id,
                    status: persisted.status || 'queued',
                    progress: persisted.progress || null,
                    params: persisted.params || null,
                });
                setRunning(true);
            }
        } catch (_err) {
            window.localStorage.removeItem(ACTIVE_PIPELINE_JOB_STORAGE_KEY);
        }
    }, []);

    useEffect(() => {
        if (!activeJob?.id) {
            window.localStorage.removeItem(ACTIVE_PIPELINE_JOB_STORAGE_KEY);
            return;
        }

        window.localStorage.setItem(
            ACTIVE_PIPELINE_JOB_STORAGE_KEY,
            JSON.stringify({
                id: activeJob.id,
                status: activeJob.status,
                progress: activeJob.progress || null,
                params: activeJob.params || null,
            })
        );
    }, [activeJob]);

    useEffect(() => {
        if (!activeJob?.id) return;

        let cancelled = false;
        let timer = null;

        const scheduleNext = (delayMs) => {
            if (cancelled) return;
            timer = window.setTimeout(() => {
                setJobPollingTick((n) => n + 1);
            }, delayMs);
        };

        const poll = async () => {
            try {
                const payload = await getPipelineJob(activeJob.id);
                if (cancelled) return;

                const latest = payload?.job || null;
                setActiveJob(latest);
                setJobWarning('');

                if (latest?.params?.pipeline) {
                    setSelectedKey((prev) => prev || latest.params.pipeline);
                }

                if (latest?.status === 'succeeded') {
                    setRunning(false);
                    const successMsg = `Pipeline complete. Saved prediction #${latest?.result?.savedPredictionId || 'new'}.`;
                    setApplyMessage(successMsg);
                    pushSuccess(successMsg);
                    await loadPredictions(aiRace);
                    await loadJobs(aiRace);
                    setActiveJob(null);
                    return;
                }

                if (latest?.status === 'failed') {
                    setRunning(false);
                    const failedMsg = latest?.error?.message || 'Pipeline run failed';
                    setError(failedMsg);
                    notify(failedMsg, 'error');
                    setActiveJob(null);
                    return;
                }

                if (latest?.status === 'canceled' || latest?.status === 'canceling') {
                    setRunning(false);
                    const cancelMsg = `Pipeline job ${latest.id} canceled.`;
                    setApplyMessage(cancelMsg);
                    pushSuccess(cancelMsg);
                    setActiveJob(null);
                    return;
                }

                setRunning(true);
                scheduleNext(3000);
            } catch (e) {
                if (cancelled) return;

                const errMsg = String(e?.message || '').toLowerCase();
                const isMissingJob = e?.status === 404 || errMsg.includes('job not found');
                if (isMissingJob) {
                    setRunning(false);
                    setActiveJob(null);
                    setJobWarning('');
                    const missingMsg = 'The previous AI job no longer exists on the server. You can start a new run.';
                    setError(missingMsg);
                    notify(missingMsg, 'error');
                    return;
                }

                setRunning(true);
                setJobWarning('Connection issue while checking job status. Retrying…');
                scheduleNext(5000);
            }
        };

        poll();

        return () => {
            cancelled = true;
            if (timer) window.clearTimeout(timer);
        };
    }, [activeJob?.id, jobPollingTick, aiRace]);

    useEffect(() => {
        getAgents()
            .then((data) => {
                setPipelines(data.pipelines || []);
            })
            .catch((e) => console.error('Failed to load AI pipelines', e));
    }, []);

    useEffect(() => {
        let mounted = true;

        async function loadAiConfig() {
            try {
                const config = await getAiRuntimeConfig();
                if (!mounted) return;
                if (config?.host) {
                    setAiHostInput(config.host);
                }
                setServerStatus((prev) => ({
                    ...prev,
                    host: config?.host || prev.host || null,
                    model: config?.model || prev.model || null,
                }));
                const status = await getAiServerStatus();
                if (!mounted) return;
                setServerStatus((prev) => ({
                    ...status,
                    host: config?.host || status?.host || prev.host || null,
                    model: config?.model || status?.model || prev.model || null,
                }));
            } catch (_e) {
                try {
                    const status = await getAiServerStatus();
                    if (!mounted) return;
                    if (status?.host) {
                        setAiHostInput(status.host);
                    }
                    setServerStatus((prev) => ({
                        ...prev,
                        ...status,
                        host: status?.host || prev.host || null,
                        model: status?.model || prev.model || null,
                    }));
                } catch (_statusErr) {
                    // Keep UI usable even if config/status checks fail.
                }
            }
        }

        loadAiConfig();
        return () => { mounted = false; };
    }, []);

    useEffect(() => {
        loadPredictions(aiRace);
        loadJobs(aiRace);
    }, [aiRace]);

    async function loadPredictions(raceId) {
        if (!raceId) {
            setPredictions([]);
            return;
        }

        setLoadingPredictions(true);
        try {
            const rows = await getAiPredictions(raceId);
            setPredictions(rows || []);
        } catch (e) {
            console.error('Failed to load AI predictions', e);
            setPredictions([]);
        } finally {
            setLoadingPredictions(false);
        }
    }

    async function loadJobs(raceId) {
        if (!raceId) {
            setJobs([]);
            return;
        }

        setLoadingJobs(true);
        try {
            const rows = await getPipelineJobs(100);
            const filtered = (rows || []).filter((job) => Number(job?.params?.raceId) === Number(raceId));
            setJobs(filtered);
        } catch (e) {
            console.error('Failed to load AI job history', e);
            setJobs([]);
        } finally {
            setLoadingJobs(false);
        }
    }

    function formatJobProgress(job) {
        if (!job) return '';
        const currentStep = Number(job?.progress?.currentStep) || 0;
        const totalSteps = Number(job?.progress?.totalSteps) || 0;
        const stepName = job?.progress?.stepName || null;
        const message = job?.progress?.message || '';

        const parts = [];
        parts.push(`Job ${job.id}`);
        parts.push(`Status: ${job.status}`);
        if (totalSteps > 0) {
            parts.push(`Step ${Math.min(currentStep, totalSteps)}/${totalSteps}`);
        }
        if (stepName) {
            parts.push(stepName);
        }
        if (message) {
            parts.push(message);
        }
        return parts.join(' • ');
    }

    async function handleDeleteActiveJob() {
        if (!activeJob?.id) return;

        const confirmed = window.confirm(`Delete/cancel AI job ${activeJob.id}?`);
        if (!confirmed) return;

        setDeletingJobId(activeJob.id);
        setError('');
        setApplyMessage('');

        try {
            await deletePipelineJob(activeJob.id);
            setRunning(false);
            setActiveJob(null);
            setJobWarning('');
            const cancelMsg = `Pipeline job ${activeJob.id} canceled.`;
            setApplyMessage(cancelMsg);
            pushSuccess(cancelMsg);
            await loadJobs(aiRace);
        } catch (e) {
            const failedMsg = e.message || 'Failed to cancel AI job';
            setError(failedMsg);
            notify(failedMsg, 'error');
        } finally {
            setDeletingJobId(null);
        }
    }

    async function handleCheckServer() {
        setError('');
        setCheckingServer(true);
        try {
            if (aiHostInput.trim()) {
                const updated = await updateAiRuntimeConfigHost(aiHostInput);
                if (updated?.host) {
                    setAiHostInput(updated.host);
                }
            }

            const status = await getAiServerStatus();
            setServerStatus(status || { online: false, message: 'AI status check failed' });
        } catch (e) {
            const failedMsg = e.message || 'Failed to check AI server';
            setError(failedMsg);
            notify(failedMsg, 'error');
        } finally {
            setCheckingServer(false);
        }
    }

    async function handleWarmupModel() {
        setWarmingModel(true);
        setWarmupMessage('');
        setError('');

        try {
            const payload = await warmupAiModel();
            const status = await getAiServerStatus();
            setServerStatus(status || { online: false, message: 'AI status check failed' });
            const warmupMsg = `Warmup complete (${payload.model || 'model'}).`;
            setWarmupMessage(warmupMsg);
            pushSuccess(warmupMsg);
        } catch (e) {
            setWarmupMessage('');
            const failedMsg = e.message || 'Model warmup failed';
            setError(failedMsg);
            notify(failedMsg, 'error');
        } finally {
            setWarmingModel(false);
        }
    }

    async function handleRun() {
        if (!selectedKey || !serverStatus.online) return;
        setRunning(true);
        setError('');
        setApplyMessage('');
        setJobWarning('');

        try {
            const payload = await runPipelineAsync(selectedKey, aiRace || null, extraNotes);
            const job = payload?.job || null;
            if (!job?.id) {
                throw new Error('Pipeline job did not return a job id');
            }
            setActiveJob(job);
        } catch (e) {
            const failedMsg = e.message || 'Run failed';
            setError(failedMsg);
            notify(failedMsg, 'error');
            setRunning(false);
        }
    }

    async function handleGeneratePrompt() {
        if (!aiRace) {
            const missingMsg = 'Select a race first';
            setError(missingMsg);
            notify(missingMsg, 'error');
            return;
        }

        setGeneratingPrompt(true);
        setError('');

        try {
            const payload = await generateOneShotPrompt(aiRace);
            setGeneratedPrompt(payload?.prompt || '');
            setGeneratedPromptMeta({
                pipeline: payload?.pipeline || 'race_analysis_one_shot',
                step: payload?.step || 'One Shot Prediction JSON',
                raceId: payload?.raceId || aiRace,
            });
        } catch (e) {
            setGeneratedPrompt('');
            setGeneratedPromptMeta(null);
            const failedMsg = e.message || 'Failed to generate prompt';
            setError(failedMsg);
            notify(failedMsg, 'error');
        } finally {
            setGeneratingPrompt(false);
        }
    }

    async function handleApplyPrediction(predictionId) {
        if (!aiRace || !predictionId) return;
        setApplyingPredictionId(predictionId);
        setError('');
        setApplyMessage('');

        try {
            const payload = await applyAiPrediction(predictionId, aiRace);
            const applyMsg = `Applied prediction #${predictionId} as ${payload.username || 'AI'} for race #${payload.raceId}.`;
            setApplyMessage(applyMsg);
            pushSuccess(applyMsg);
        } catch (e) {
            const failedMsg = e.message || 'Failed to apply prediction';
            setError(failedMsg);
            notify(failedMsg, 'error');
        } finally {
            setApplyingPredictionId(null);
        }
    }

    async function handleDeletePrediction(predictionId) {
        if (!aiRace || !predictionId) return;

        const confirmed = window.confirm(`Delete AI prediction #${predictionId}? This cannot be undone.`);
        if (!confirmed) return;

        setDeletingPredictionId(predictionId);
        setError('');
        setApplyMessage('');

        try {
            await deleteAiPrediction(predictionId);
            await loadPredictions(aiRace);
        } catch (e) {
            const failedMsg = e.message || 'Failed to delete prediction';
            setError(failedMsg);
            notify(failedMsg, 'error');
        } finally {
            setDeletingPredictionId(null);
        }
    }

    const currentOption = pipelines.find((o) => o.key === selectedKey);

    return (
        <div className="card mb-4">
            <div className="card-body">
                <h5 className="card-title mb-3">AI Super Admin</h5>

                <div className="row g-3 mb-4">
                    <div className="col-md-6">
                        <label className="form-label">AI Race Selector</label>
                        <RaceSelector value={aiRace} onChange={setAiRace} />
                    </div>
                    <div className="col-md-6">
                        <label className="form-label">AI Server Host</label>
                        <div className="input-group">
                            <input
                                className="form-control"
                                type="text"
                                placeholder="http://192.168.1.34:11435"
                                value={aiHostInput}
                                onChange={(e) => setAiHostInput(e.target.value)}
                                disabled={checkingServer || warmingModel}
                            />
                            <button
                                className="btn btn-outline-secondary"
                                type="button"
                                onClick={handleCheckServer}
                                disabled={checkingServer || running || warmingModel}
                            >
                                {checkingServer ? 'Checking…' : 'Check'}
                            </button>
                        </div>
                    </div>
                </div>

                <div className="mb-3 d-flex flex-wrap gap-2 align-items-center">
                    <button
                        className="btn btn-outline-warning"
                        type="button"
                        onClick={handleWarmupModel}
                        disabled={warmingModel || running || checkingServer}
                    >
                        {warmingModel ? 'Warming…' : 'Warm Up Model'}
                    </button>
                    <span className={`small ${serverStatus.online ? 'text-success' : 'text-muted'}`}>
                        {serverStatus.online ? 'Online' : 'Offline'}
                        {serverStatus.host ? ` • ${serverStatus.host}` : ''}
                        {serverStatus.model ? ` • model: ${serverStatus.model}` : ''}
                        {!serverStatus.online && serverStatus.message ? ` • ${serverStatus.message}` : ''}
                    </span>
                </div>

                {warmupMessage && <div className="alert alert-success py-2">{warmupMessage}</div>}
                {applyMessage && <div className="alert alert-success py-2">{applyMessage}</div>}
                {error && <div className="alert alert-danger py-2">{error}</div>}

                {activeJob ? (
                    <div className="d-flex gap-2 align-items-center mb-3">
                        <div className="small text-primary">{formatJobProgress(activeJob)}</div>
                        <button
                            className="btn btn-sm btn-outline-danger"
                            type="button"
                            onClick={handleDeleteActiveJob}
                            disabled={deletingJobId === activeJob.id}
                        >
                            {deletingJobId === activeJob.id ? 'Canceling…' : 'Cancel Job'}
                        </button>
                    </div>
                ) : null}
                {jobWarning ? <div className="small text-warning mb-3">{jobWarning}</div> : null}

                <div className="row g-3 mb-3">
                    <div className="col-md-7">
                        <label className="form-label">Pipeline</label>
                        <select
                            className="form-select"
                            value={selectedKey}
                            onChange={(e) => { setSelectedKey(e.target.value); setError(''); }}
                        >
                            <option value="">— select pipeline —</option>
                            {pipelines.map((o) => (
                                <option key={o.key} value={o.key}>
                                    {o.name}{o.stepCount ? ` (${o.stepCount} steps)` : ''}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="col-md-5 d-flex align-items-end gap-2">
                        <button
                            className="btn btn-primary w-100"
                            type="button"
                            disabled={!selectedKey || running || !serverStatus.online || Boolean(activeJob?.id)}
                            onClick={handleRun}
                        >
                            {running ? 'Running…' : 'Run Pipeline'}
                        </button>
                    </div>
                </div>

                <div className="mb-3">
                    <label className="form-label">Extra Notes</label>
                    <textarea
                        className="form-control"
                        rows={3}
                        placeholder="Optional context for this run"
                        value={extraNotes}
                        onChange={(e) => setExtraNotes(e.target.value)}
                        disabled={running}
                    />
                </div>

                <div className="d-flex gap-2 align-items-center mb-3">
                    <button
                        className="btn btn-outline-primary"
                        type="button"
                        disabled={!aiRace || generatingPrompt}
                        onClick={handleGeneratePrompt}
                    >
                        {generatingPrompt ? 'Generating…' : 'Generate One-Shot Prompt'}
                    </button>
                    {currentOption && <p className="text-muted small mb-0">{currentOption.description}</p>}
                </div>

                {generatedPrompt ? (
                    <details className="mb-3">
                        <summary className="fw-semibold">Generated Prompt Preview</summary>
                        <div className="p-3 bg-dark border rounded mt-2">
                            <div className="small text-muted mb-2">
                                Prompt Preview • {generatedPromptMeta?.pipeline} • {generatedPromptMeta?.step} • race #{generatedPromptMeta?.raceId}
                            </div>
                            <pre className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>{generatedPrompt}</pre>
                        </div>
                    </details>
                ) : null}

                <div className="mb-4">
                    <h6 className="mb-3">AI Request History</h6>
                    {(loadingPredictions || loadingJobs) && <div className="small text-muted mb-3">Loading AI history…</div>}
                    {!aiRace && <div className="small text-muted">Select a race to view AI request history.</div>}
                    {aiRace && !loadingPredictions && !loadingJobs && predictions.length === 0 && jobs.length === 0 && (
                        <div className="small text-muted">No saved AI predictions or pipeline jobs for this race yet.</div>
                    )}
                    {aiRace && (predictions.length > 0 || jobs.length > 0) ? (
                        <div className="table-responsive">
                            <table className="table table-sm align-middle">
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>Type</th>
                                        <th>Prompt Type</th>
                                        <th>Status</th>
                                        <th>Error</th>
                                        <th>Created</th>
                                        <th>Details</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {predictions.map((row) => (
                                        <tr key={`pred-${row.id}`}>
                                            <td>#{row.id}</td>
                                            <td>Prediction</td>
                                            <td>{row.runKey || row.runType || 'unknown'}</td>
                                            <td><span className="badge bg-success">succeeded</span></td>
                                            <td>—</td>
                                            <td>{row.createdAt}</td>
                                            <td>
                                                <details>
                                                    <summary className="small">View</summary>
                                                    <div className="mt-2">
                                                        <div className="mb-2">
                                                            <strong>Prompt</strong>
                                                            <pre className="p-2 bg-dark border rounded" style={{ whiteSpace: 'pre-wrap' }}>{getPromptText(row) || 'No prompt stored'}</pre>
                                                        </div>
                                                        <div>
                                                            <strong>Result</strong>
                                                            <pre className="p-2 bg-dark border rounded" style={{ whiteSpace: 'pre-wrap' }}>{getResultText(row) || 'No result available'}</pre>
                                                        </div>
                                                        <div className="mt-2 d-flex gap-2 flex-wrap">
                                                            <button
                                                                className="btn btn-sm btn-outline-success"
                                                                type="button"
                                                                onClick={() => handleApplyPrediction(row.id)}
                                                                disabled={applyingPredictionId === row.id || deletingPredictionId === row.id}
                                                            >
                                                                {applyingPredictionId === row.id ? 'Applying…' : 'Apply'}
                                                            </button>
                                                            <button
                                                                className="btn btn-sm btn-outline-danger"
                                                                type="button"
                                                                onClick={() => handleDeletePrediction(row.id)}
                                                                disabled={deletingPredictionId === row.id || applyingPredictionId === row.id}
                                                            >
                                                                {deletingPredictionId === row.id ? 'Deleting…' : 'Delete'}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </details>
                                            </td>
                                        </tr>
                                    ))}
                                    {jobs.map((job) => (
                                        <tr key={`job-${job.id}`}>
                                            <td>job {job.id}</td>
                                            <td>Pipeline job</td>
                                            <td>{job?.params?.pipeline || 'unknown'}</td>
                                            <td>
                                                <span className={`badge ${job.status === 'failed' ? 'bg-danger' : job.status === 'succeeded' ? 'bg-success' : job.status === 'canceled' ? 'bg-secondary' : 'bg-warning'}`}>
                                                    {job.status}
                                                </span>
                                            </td>
                                            <td>{job?.error?.message || '—'}</td>
                                            <td>{job.createdAt}</td>
                                            <td>
                                                <details>
                                                    <summary className="small">View</summary>
                                                    <div className="mt-2">
                                                        <div className="mb-2">
                                                            <strong>Pipeline</strong>
                                                            <div className="small text-muted">{job?.params?.pipeline || 'unknown'}</div>
                                                        </div>
                                                        <div className="mb-2">
                                                            <strong>Prompt / Notes</strong>
                                                            <pre className="p-2 bg-dark border rounded" style={{ whiteSpace: 'pre-wrap' }}>{job?.params?.extraNotes || 'No extra notes'}</pre>
                                                        </div>
                                                        {job?.result ? (
                                                            <div className="mb-2">
                                                                <strong>Result</strong>
                                                                <pre className="p-2 bg-dark border rounded" style={{ whiteSpace: 'pre-wrap' }}>{safeJsonString(job.result)}</pre>
                                                            </div>
                                                        ) : null}
                                                        {job?.error?.details ? (
                                                            <div className="mb-2">
                                                                <strong>Error details</strong>
                                                                <pre className="p-2 bg-dark border rounded" style={{ whiteSpace: 'pre-wrap' }}>{safeJsonString(job.error.details)}</pre>
                                                            </div>
                                                        ) : null}
                                                    </div>
                                                </details>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : null}
                </div>

                <AiPredictionPerformanceChart raceId={aiRace} predictions={predictions} />
            </div>
        </div>
    );
}
